import type { ReactNode } from "react";
import { Icon } from "@sx/ui";
import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { Button, EmptyState, PageHeader } from "@sx/ui";

import { isNavigable } from "../lib/nav";

/* Nowhere, in the two ways the console has of being nowhere.
 *
 * One frame and one way back — six apps each had their own, and catalog even had a
 * `/not-found` *route* it redirected to, which put a dead address in the history stack.
 * What differs between the two is only what is true:
 *
 *   `NotFound`     the address is not one of ours. Stale link, typo, a page that moved.
 *   `NotBuiltYet`  the address is ours and there is nothing behind it. The console has
 *                  route files for more of the product than the product has, and
 *                  `docs/quality-ledger.yaml` says which of them a person may open
 *                  (north-star 2.9). A page that renders a shape over nothing is the
 *                  pretence that step ends, so the address is shut — by URL and in
 *                  navigation alike, and nothing is lost: the ledger page lists every
 *                  shut address as the backlog with the sentence that closed it.
 *
 * A person arriving from a bookmark is owed the difference between those two, which is
 * why they are two sentences and not one.
 */
function Nowhere({
  bar,
  title,
  hint,
}: {
  readonly bar: string;
  readonly title: string;
  readonly hint: string;
}) {
  return (
    <div className="grid h-full place-items-center p-6">
      {/* The top bar names where the person is, not the area the address started in. */}
      <PageHeader title={bar} />
      <EmptyState
        icon={<Icon name="explore" tone="accent" />}
        title={title}
        hint={hint}
        action={
          <Button asChild size="sm">
            <Link to="/">Go to your workspace</Link>
          </Button>
        }
      />
    </div>
  );
}

export function NotFound() {
  return (
    <Nowhere
      bar="Page not found"
      title="This page does not exist"
      hint="The address may be stale, or the workspace may have moved it."
    />
  );
}

function NotBuiltYet({ address }: { readonly address: string }): ReactNode {
  return (
    <Nowhere
      bar="Not built yet"
      title="Not built yet"
      hint={`There is no page at ${address}. Nothing is hidden from you — this part of the product is not made yet.`}
    />
  );
}

/** The one gate on the console's address space.
 *
 * Every route renders through it, so an address cannot be opened by typing it, by a stale
 * link, or by a redirect that outlived its destination.
 *
 * It sits at the root, which puts it **above** the session gate in `_console.tsx`: a
 * signed-out person who opens a shut address is told it is not built rather than sent to
 * sign in first. That is deliberate, and it is the reason the gate is one place instead of
 * two — whatever is mounted outside the console layout must not be able to open on its own.
 * Nothing is disclosed by it: every address in this set is already in the route tree the
 * browser downloads. A shut address also draws no chrome, because chrome belongs to a
 * workspace and the walk back is the only thing this screen has to offer.
 *
 * Nothing runs behind it. None of the shut routes declares a `loader` or a `beforeLoad`;
 * their reads live in components that never mount.
 *
 * Two keys, and they are the same key. The matched route's own `fullPath` — the template,
 * `/p/$org/$project/library/map` — is what the ledger answers for; the person's own
 * pathname is what the screen says back to them. An address that matched no route at all
 * has no ledger answer and is left to the router's own not-found.
 */
export function NavigableOutlet() {
  const { matched, pathname } = useRouterState({
    select: (state) => ({
      matched: state.matches.at(-1)?.fullPath,
      pathname: state.location.pathname,
    }),
  });
  if (matched !== undefined && !isNavigable(matched)) {
    return <NotBuiltYet address={pathname} />;
  }
  return <Outlet />;
}
